import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/**
 * Issue list normalization, project map lookup, grouping, and stable keys.
 */

/**
 * Normalize value from content path to an array of issue rows.
 * Accepts array directly or object with issues/key array.
 * @param {Object|Array} value - Raw value at content path
 * @returns {Array} Array of issue objects
 */
export const getIssuesArray = value => {
  var _ref, _ref2, _ref3, _value$issues;
  if (value == null) return [];
  if (Array.isArray(value)) return value;
  const list = (_ref = (_ref2 = (_ref3 = (_value$issues = value.issues) !== null && _value$issues !== void 0 ? _value$issues : value.tvcIssues) !== null && _ref3 !== void 0 ? _ref3 : value.data) !== null && _ref2 !== void 0 ? _ref2 : value.items) !== null && _ref !== void 0 ? _ref : [];
  return Array.isArray(list) ? list : [];
};

/**
 * Project row from the content map for a &lt;select&gt; value. Tries map key, case-insensitive key,
 * then row.uuid (when options used inner uuid but map keys differ).
 * @param {Object|null|undefined} projectsMap
 * @param {string} selectedValue
 * @returns {Object|null}
 */
export const getProjectRowFromMap = (projectsMap, selectedValue) => {
  if (projectsMap == null || typeof projectsMap !== 'object' || Array.isArray(projectsMap)) {
    return null;
  }
  if (selectedValue == null || String(selectedValue).trim() === '') {
    return null;
  }
  const sk = String(selectedValue);
  const direct = projectsMap[sk];
  if (direct != null && typeof direct === 'object' && !Array.isArray(direct)) {
    return direct;
  }
  const keys = Object.keys(projectsMap);
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    if (String(k) === sk || String(k).toLowerCase() === sk.toLowerCase()) {
      const row = projectsMap[k];
      if (row != null && typeof row === 'object' && !Array.isArray(row)) {
        return row;
      }
    }
  }
  for (let i = 0; i < keys.length; i += 1) {
    var _row$uuid;
    const row = projectsMap[keys[i]];
    if (row != null && typeof row === 'object' && !Array.isArray(row) && String((_row$uuid = row.uuid) !== null && _row$uuid !== void 0 ? _row$uuid : '') === sk) {
      return row;
    }
  }
  return null;
};

/**
 * Stable key for a list item (accordion / React key).
 * @param {Object} item - Issue row
 * @param {number} index - Fallback when rowTitle missing
 * @returns {string}
 */
export const getTvcListItemRowKey = (item, index) => {
  var _item$formID, _item$errorType, _item$rowData$rowTitl, _item$rowData;
  return "".concat((_item$formID = item.formID) !== null && _item$formID !== void 0 ? _item$formID : 'id', "-").concat((_item$errorType = item.errorType) !== null && _item$errorType !== void 0 ? _item$errorType : 'err', "-").concat((_item$rowData$rowTitl = (_item$rowData = item.rowData) === null || _item$rowData === void 0 ? void 0 : _item$rowData.rowTitle) !== null && _item$rowData$rowTitl !== void 0 ? _item$rowData$rowTitl : index);
};

/**
 * Stable id for an issue row (no list index). Used for optimistic remove / animations.
 * @param {Object} item - Issue row
 * @returns {string}
 */
export const getTvcIssueStableRowKey = item => {
  var _ref4, _ref5, _item$rowData$rowTitl2, _item$rowData2, _item$formID2, _item$errorType2;
  const title = (_ref4 = (_ref5 = (_item$rowData$rowTitl2 = (_item$rowData2 = item.rowData) === null || _item$rowData2 === void 0 ? void 0 : _item$rowData2.rowTitle) !== null && _item$rowData$rowTitl2 !== void 0 ? _item$rowData$rowTitl2 : item.rowTitle) !== null && _ref5 !== void 0 ? _ref5 : item.errorDetails) !== null && _ref4 !== void 0 ? _ref4 : '';
  return "".concat((_item$formID2 = item.formID) !== null && _item$formID2 !== void 0 ? _item$formID2 : 'id', "-").concat((_item$errorType2 = item.errorType) !== null && _item$errorType2 !== void 0 ? _item$errorType2 : 'err', "-").concat(title);
};

/** Normalized project name for a row (used for grouping and filtering). */
const getProjectKey = row => {
  var _row$projectName;
  return String((_row$projectName = row === null || row === void 0 ? void 0 : row.projectName) !== null && _row$projectName !== void 0 ? _row$projectName : '').trim();
};

/** Normalized asset type for a row (used for grouping and filtering). */
const getAssetKey = row => {
  var _row$assetType;
  return String((_row$assetType = row === null || row === void 0 ? void 0 : row.assetType) !== null && _row$assetType !== void 0 ? _row$assetType : 'Other').trim();
};

/** Normalized error type for a row (used for grouping and filtering). */
const getErrorKey = row => {
  var _ref6, _row$errorType;
  const raw = (_ref6 = (_row$errorType = row === null || row === void 0 ? void 0 : row.errorType) !== null && _row$errorType !== void 0 ? _row$errorType : row === null || row === void 0 ? void 0 : row.errorDetails) !== null && _ref6 !== void 0 ? _ref6 : '';
  const s = String(raw).trim();
  return s || 'Other';
};

/**
 * Unique project names from issues, sorted.
 * @param {Array} issues - Array of issue objects with projectName
 * @returns {Array<string>}
 */
export const getUniqueProjects = issues => {
  const set = new Set();
  issues.forEach(row => {
    const name = row === null || row === void 0 ? void 0 : row.projectName;
    if (name != null && String(name).trim()) set.add(String(name).trim());
  });
  return [...set].sort((a, b) => a.localeCompare(b, undefined, {
    sensitivity: 'base'
  }));
};

/**
 * Group issues for a given project by assetType, then by errorType, with counts.
 * @param {Array} issues - Full issues array
 * @param {string} projectName - Selected project (null = all)
 * @returns {{ assetTypes: Array<{ name: string, count: number,
 *   errorTypes: Array<{ name: string, count: number }> }> }}
 */
export const getGroupedChecksForProject = (issues, projectName) => {
  const projKey = projectName ? String(projectName).trim() : null;
  const filtered = projKey ? issues.filter(row => getProjectKey(row) === projKey) : issues;
  const byAsset = {};
  filtered.forEach(row => {
    var _byAsset$asset$byErro;
    const asset = getAssetKey(row);
    const err = getErrorKey(row);
    if (!byAsset[asset]) {
      byAsset[asset] = {
        count: 0,
        byError: {}
      };
    }
    byAsset[asset].count += 1;
    byAsset[asset].byError[err] = ((_byAsset$asset$byErro = byAsset[asset].byError[err]) !== null && _byAsset$asset$byErro !== void 0 ? _byAsset$asset$byErro : 0) + 1;
  });
  const assetNames = Object.keys(byAsset).sort((a, b) => a.localeCompare(b, undefined, {
    sensitivity: 'base'
  }));
  return {
    assetTypes: assetNames.map(name => {
      const _byAsset$name = byAsset[name],
        count = _byAsset$name.count,
        byError = _byAsset$name.byError;
      const errorTypes = Object.entries(byError).map(_ref7 => {
        let _ref8 = _slicedToArray(_ref7, 2),
          errorName = _ref8[0],
          errorCount = _ref8[1];
        return {
          name: errorName,
          count: errorCount
        };
      }).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, undefined, {
        sensitivity: 'base'
      }));
      return {
        name,
        count,
        errorTypes
      };
    })
  };
};

/**
 * Filter issues by Project -> Asset Type -> Error Type (one "check").
 * @param {Array} issues - Full issues array
 * @param {string} projectName - Project name
 * @param {string} assetType - Asset type
 * @param {string} errorType - Error type
 * @returns {Array} Matching issue objects
 */
export const getIssuesForCheck = (issues, projectName, assetType, errorType) => {
  if (!projectName || !assetType || !errorType) return [];
  const proj = String(projectName).trim();
  const asset = String(assetType).trim();
  const err = String(errorType).trim();
  return issues.filter(row => getProjectKey(row) === proj && getAssetKey(row) === asset && getErrorKey(row) === err);
};